// What this device keeps of what it has read, so a screen opened with no
// connection draws what it last showed, said to be from then (scenario M4,
// DIRECTION U16, B-557, PLAN-2026-10-29-game-day-with-no-signal §1).
//
// WHY THE DEVICE AND NOT THE PAGE. The shared reads (`readStore.ts`) hold
// their answers for the page's life, so a screen that loses its connection
// keeps what it drew (B-222). An app reopened in a tunnel had none of that:
// the shell came up (INSTALL.md §7) and every screen said "You're offline"
// where its numbers had been.
//
// THE RULES, each a guard against showing something false:
//
//   A COPY IS NEVER AN ANSWER. It is found only when a read got no answer,
//   or one that says only "not now", and it goes to the screen as that
//   failure's `lastValue`, which is drawn with the sentence saying when it is
//   from (`FromLeague`). An answer replaces it; a refusal that says the thing
//   is gone or not yours deletes it (`GONE`), so a league somebody was taken
//   out of is not drawn from here. One that says only "not now" — a server
//   having a bad minute, too many requests — leaves it, and it is drawn
//   (`NOT_NOW`): a site that is down is the case it is kept for.
//
//   ONE ACCOUNT'S. Every copy is filed under the account that read it, and
//   one written for an account that has since left is not written: the
//   account is taken when the request leaves and compared when it lands.
//   `forget` deletes them all, at a sign-out and at any change of account.
//
//   STORAGE THAT FAILS CHANGES NOTHING. Private browsing, a full disk or
//   blocked storage keep nothing, and every read works as it did. No error
//   from here reaches a screen.
//
// No browser here: the storage is handed in (`keptIdb.ts` in the app,
// a map in `kept.test.ts`), so the rules are tested as calls.

/** How many answers are kept. Past that the oldest written go first. */
export const KEPT_CAPACITY = 200;

/**
 * How long a lookup may take before the read goes on without it. IndexedDB
 * that never opens is a known fault on some iPhones, and a read must not wait
 * on it for ever.
 */
export const FIND_MS = 1_000;

/** Refusals that say the thing is gone or not this person's: the copy is deleted. */
export const GONE: ReadonlySet<string> = new Set(['unauthorized', 'forbidden', 'not_found']);

/** Refusals that say only "not now": the copy stays, and is drawn. */
export const NOT_NOW: ReadonlySet<string> = new Set(['unavailable', 'rate_limited']);

/** A kept answer: the body as it came, and the instant it was true. */
export interface KeptCopy {
  data: unknown;
  asOf: string;
}

/** Where copies live. Every method may reject; the rules below swallow it. */
export interface KeptStorage {
  get(key: string): Promise<KeptCopy | null>;
  put(key: string, copy: KeptCopy): Promise<void>;
  delete(key: string): Promise<void>;
  /** Delete the oldest written until no more than `capacity` remain. */
  trim(capacity: number): Promise<void>;
  clear(): Promise<void>;
}

export interface Kept {
  /** Whose reads are kept from now on; null keeps and finds nothing. */
  setAccount(account: string | null): void;
  /** The account now, taken when a request leaves and handed back with its answer. */
  account(): string | null;
  /** Keep this answer for `name` (a path), if `account` is still the one signed in. */
  keep(account: string | null, name: string, copy: KeptCopy): void;
  /** The copy kept for `name`, if there is one for this account. */
  find(account: string | null, name: string): Promise<KeptCopy | null>;
  /** The server refused `name`: its copy is not drawn again. */
  drop(account: string | null, name: string): void;
  /** Delete every copy, whoever's. Settles when they are gone. */
  forget(): Promise<void>;
}

export interface KeptOptions {
  /** Null where there is none: nothing is kept and nothing found. */
  storage: KeptStorage | null;
  /** Run `fn` once `ms` have passed; returns the way to cancel it. Bounds a lookup. */
  after?: (ms: number, fn: () => void) => () => void;
  capacity?: number;
  /** Trim after this many writes, rather than counting on every one. */
  trimEvery?: number;
}

export function createKept(options: KeptOptions): Kept {
  const storage = options.storage;
  const capacity = options.capacity ?? KEPT_CAPACITY;
  const trimEvery = options.trimEvery ?? 20;
  const after =
    options.after ??
    ((ms: number, fn: () => void) => {
      const timer = setTimeout(fn, ms);
      return () => clearTimeout(timer);
    });
  let current: string | null = null;
  let writes = 0;
  // Writes queue behind a `forget` in flight, so a copy kept a moment after a
  // sign-out started is not left behind by the clear that was meant to take it.
  let settled: Promise<void> = Promise.resolve();

  // Length-prefixed, as `identityOf` is, so no account and name can spell
  // another pair's key.
  const keyOf = (account: string, name: string) => `${String(account.length)}:${account}${name}`;
  const quietly = (work: () => Promise<void>) => {
    settled = settled.then(work).catch(() => undefined);
  };

  return {
    setAccount(account) {
      current = account;
    },

    account() {
      return current;
    },

    keep(account, name, copy) {
      if (storage === null || account === null || account !== current) return;
      quietly(async () => {
        // Asked again once the queue reaches it: a sign-out in between wins.
        if (account !== current) return;
        await storage.put(keyOf(account, name), copy);
        writes += 1;
        if (writes % trimEvery === 0) await storage.trim(capacity);
      });
    },

    find(account, name) {
      if (storage === null || account === null || account !== current) return Promise.resolve(null);
      const lookup = (async () => {
        await settled;
        if (account !== current) return null;
        try {
          return await storage.get(keyOf(account, name));
        } catch {
          return null;
        }
      })();
      // Storage that does not answer is storage that has nothing.
      return new Promise<KeptCopy | null>((resolve) => {
        const cancel = after(FIND_MS, () => resolve(null));
        void lookup.then((copy) => {
          cancel();
          resolve(copy);
        });
      });
    },

    drop(account, name) {
      if (storage === null || account === null) return;
      quietly(() => storage.delete(keyOf(account, name)));
    },

    forget() {
      if (storage !== null) quietly(() => storage.clear());
      return settled;
    },
  };
}
